import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import InfoTip from '../components/InfoTip';

/** 视图切换。标签短、一次点中，是这一页唯一的导航 */
const VIEWS = [
  { value: 'list', label: '📋 用药记录' },
  { value: 'calendar', label: '📅 日历' },
  { value: 'logs', label: '🔔 提醒日志' },
];

/**
 * 状态筛选用大按钮而不是下拉：
 * 下拉要点两下（展开→选中）还要在小字里找，按钮一下点中、字也大。
 */
const STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'taken', label: '已服用' },
  { value: 'missed', label: '已错过' },
  { value: 'skipped', label: '已跳过' },
];

/**
 * 事件类型筛选项，值与服务端的 event 一致。
 * 标签一律压到 2 个字：这一栏在「提醒日志」里要和日期、排序挤在同一行，
 * 原来「全部事件 / 只看漏服 / 到点提醒」这种 4 字标签，三个下拉并排时
 * 每个都会被自己的下拉箭头切掉尾巴。语义没变，只是不再重复「事件」二字
 * —— 这一栏本来就是事件筛选，重复一遍没有新增信息。
 */
const EVENT_OPTIONS = [
  { value: 'all', label: '全部' },
  { value: 'missed', label: '漏服' },
  { value: 'triggered', label: '到点' },
  { value: 'delivered', label: '送达' },
  { value: 'snoozed', label: '稍后' },
  { value: 'confirmed', label: '确认' },
  { value: 'skipped', label: '跳过' },
];

/**
 * 记录行左边那一列时间。
 * 记录表里只有 actual_time（实际吃的时间）和 scheduled_time（计划时间），
 * 没有 record_time 这个字段——之前这里读的是 record_time，所以时间列一直是空的。
 * 吃了 → 显示实际时间；漏了 / 跳过 → 实际时间为空，退回计划时间。
 * 两者都是 HH:MM，长度一致，才能对齐成一列。
 */
const timeOf = (r) => String(r.actual_time || r.scheduled_time || '—').slice(0, 5);

/**
 * 日期显示：当天的年份就是今年时省掉年份。
 * 「2026-09-15」在 16px 字号下约占 110px，右边再挂一句汇总就必然折行；
 * 缩成「9月15日」后日期和汇总能落在同一行。跨年的记录仍带年份，避免误读。
 * （用药记录和提醒日志的分组头共用这一个格式化函数。）
 */
const dayLabel = (date) => {
  const m = String(date || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return date;
  const [, y, mm, dd] = m;
  const md = `${Number(mm)}月${Number(dd)}日`;
  return y === String(new Date().getFullYear()) ? md : `${y}年${md}`;
};

export default function Records() {
  const [view, setView] = useState('list'); // list | calendar | logs
  const [records, setRecords] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);

  // 「看哪种药」下拉（用户要求加回来）。
  // 和状态按钮是**「与」关系**：先按药品收窄，再点「已服用 / 已错过 / 已跳过」
  // 切换看同一种药的不同服用记录 —— 这才是它真正有用的地方
  // （「这种药这个月漏了几次」），单独的药品筛选反而只会把长列表缩成一条。
  //
  // 布局上它**不跟**四个状态按钮挤一行：四个按钮每个 3 个字，内边距已经收到 8/8，
  // 再加一个下拉在 360px 屏上必然把某个按钮挤到第二行（手机开「超大号」时更早）。
  // 所以它单独占一行放在按钮上方 —— 一行换来的是一栏能点中的大按钮。
  const [statusFilter, setStatusFilter] = useState('');
  const [medicineFilter, setMedicineFilter] = useState('');

  // 下拉选项从**记录本身**提取，不用 api.getMedicines()：
  // 删除药品时用药记录是保留的（服务端返回「药品已删除，用药记录已保留」），
  // 所以记录里完全可能存在一个已经不在药品列表里的药名 ——
  // 用药品列表当选项，那些历史记录就永远筛不出来了。
  const [medicineOptions, setMedicineOptions] = useState([]);

  useEffect(() => {
    api.getRecords('?sort=date_desc')
      .then(list => {
        const names = Array.from(
          new Set((list || []).map(r => r.medicine_name).filter(Boolean))
        );
        // 按拼音排，中文药名按 A-Z 排会变成乱序
        names.sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'));
        setMedicineOptions(names);
      })
      .catch(() => {});
  }, []);

  // 日历
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(null);

  // 提醒日志
  const [logs, setLogs] = useState([]);
  const [logDays, setLogDays] = useState([]);
  const [logDate, setLogDate] = useState('');
  const [logEvent, setLogEvent] = useState('all');
  const [logSort, setLogSort] = useState('time_desc');

  useEffect(() => {
    if (view === 'list') loadRecords();
    else if (view === 'calendar') loadCalendar();
    else loadLogs();
  }, [view, statusFilter, medicineFilter, calYear, calMonth, logDate, logEvent, logSort]);

  const loadRecords = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.append('status', statusFilter);
    // medicineName 是整名精确匹配（服务端与演示版数据层同一套语义）：
    // 用模糊的 search 参数会把「阿司匹林肠溶片」也算进「阿司匹林」，那就筛错药了
    if (medicineFilter) params.append('medicineName', medicineFilter);
    params.append('sort', 'date_desc');
    api.getRecords(`?${params.toString()}`)
      .then(data => { setRecords(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const loadCalendar = () => {
    setLoading(true);
    api.getRecordCalendar(calYear, calMonth)
      .then(data => { setCalendarData(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const loadLogs = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (logDate) params.append('date', logDate);
    if (logEvent && logEvent !== 'all') params.append('event', logEvent);
    params.append('sort', logSort);
    Promise.all([
      api.getReminderLogs(`?${params.toString()}`),
      api.getReminderLogDays(),
    ])
      .then(([logList, days]) => {
        setLogs(logList);
        setLogDays(days || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const statusTag = (status) => {
    const map = { taken: 'tag-success', missed: 'tag-danger', skipped: 'tag-warning' };
    const labelMap = { taken: '已服用', missed: '已错过', skipped: '已跳过' };
    return (
      <span
        className={`tag ${map[status] || 'tag-info'}`}
        style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        {labelMap[status] || status}
      </span>
    );
  };

  const logTone = (event) => {
    if (event === 'missed') return 'rl-danger';
    if (event === 'snoozed') return 'rl-warning';
    if (event === 'confirmed') return 'rl-success';
    if (event === 'skipped') return 'rl-warning';
    return 'rl-info';
  };

  // 按天分组。用药记录和提醒日志都用这套骨架（rl-day-head / rl-row）：
  // 一条记录一张卡的话，15 天的数据就是 60 张卡堆成的滚动墙，没法看。
  const groupedRecords = records.reduce((acc, r) => {
    if (!acc[r.record_date]) acc[r.record_date] = [];
    acc[r.record_date].push(r);
    return acc;
  }, {});

  const groupedLogs = logs.reduce((acc, l) => {
    if (!acc[l.log_date]) acc[l.log_date] = [];
    acc[l.log_date].push(l);
    return acc;
  }, {});

  const hasFilter = Boolean(statusFilter) || Boolean(medicineFilter);

  const daysInMonth = new Date(calYear, calMonth, 0).getDate();
  const firstDay = new Date(calYear, calMonth - 1, 1).getDay();
  const today = new Date();
  const isToday = (day) => today.getFullYear() === calYear && today.getMonth() + 1 === calMonth && today.getDate() === day;

  return (
    <div className="app-container">
      {/* 标题带上「提醒」，因为这个页面现在同时装结果（吃了没）和过程（系统响了没） */}
      <h1 className="page-title">用药与提醒记录</h1>

      {/* 视图切换。flexWrap + whiteSpace:nowrap：
          超大号字号下三个按钮一行放不下时，第三个整块落到下一行，
          而不是每个按钮内部折成两行、三个高度还不一样 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {VIEWS.map(v => (
          <button
            key={v.value}
            className={view === v.value ? 'btn-primary' : 'btn-secondary'}
            style={{
              flex: '1 1 30%',
              minHeight: 48,
              padding: '8px 6px',
              fontSize: 'var(--font-size-small)',
              whiteSpace: 'nowrap',
            }}
            onClick={() => setView(v.value)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">加载中...</div>
      ) : view === 'list' ? (
        <>
          {/* 药品筛选：单独一行，放在状态按钮上方。
              两个筛选是「与」关系 —— 选了一种药之后，再点状态按钮就能看到
              「这种药漏了几次」「这种药吃了几次」，不用来回切页面。 */}
          <div className="card">
            <div className="select-row" style={{ marginBottom: 10 }}>
              <select
                className="input-field"
                aria-label="按药品筛选"
                value={medicineFilter}
                onChange={e => setMedicineFilter(e.target.value)}
              >
                <option value="">全部药品</option>
                {medicineOptions.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            <div className="filter-row">
              {STATUS_OPTIONS.map(o => (
                <button
                  key={o.value}
                  style={{
                    borderRadius: 8,
                    background: statusFilter === o.value ? 'var(--primary-color)' : 'var(--border-color)',
                    color: statusFilter === o.value ? 'white' : 'var(--text-primary)',
                    fontWeight: 600,
                    minHeight: 44,
                    fontSize: 'var(--font-size-small)',
                  }}
                  onClick={() => setStatusFilter(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* 记录列表：按天分组 */}
          {records.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <p>{hasFilter ? '没有找到对应的记录' : '还没有用药记录哦'}</p>
                {hasFilter && (
                  <p style={{ marginTop: 8, fontSize: 'var(--font-size-small)' }}>
                    换个条件，或点「全部」看看
                  </p>
                )}
              </div>
            </div>
          ) : (
            Object.entries(groupedRecords).map(([date, items]) => {
              const takenCount = items.filter(i => i.status === 'taken').length;
              const missedCount = items.filter(i => i.status === 'missed').length;
              return (
                <div key={date} className="card">
                  <div className="rl-day-head">
                    <span>{dayLabel(date)}</span>
                    {/* 汇总改成「总数在前，再分别列已服用 / 漏服」。
                        原来写「4/5 次已服用 1 次漏服」：分母 5 里已经含了那 1 次漏服，
                        右边又单独说一遍，同一个数字被算两次，读起来像重复计数。
                        现在每个数字只出现一次：「5 次 · 已服用 4 · 漏服 1」。 */}
                    <span className="rl-day-count">
                      {items.length} 次 · 已服用 {takenCount}
                      {missedCount > 0 && (
                        <b style={{ color: 'var(--danger-color)' }}> · 漏服 {missedCount}</b>
                      )}
                    </span>
                  </div>
                  {items.map(r => (
                    <div key={r.id} className="rl-row">
                      <span className="rl-time">{timeOf(r)}</span>
                      <span className="rl-body">
                        <b>{r.medicine_name}</b>
                        {r.dosage ? ` · ${r.dosage} ${r.unit || '片'}` : ''}
                      </span>
                      {statusTag(r.status)}
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </>
      ) : view === 'calendar' ? (
        /* 日历视图 */
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <button
              className="btn-secondary"
              style={{ width: 'auto', padding: '8px 16px', minHeight: 44 }}
              onClick={() => {
                if (calMonth === 1) { setCalYear(calYear - 1); setCalMonth(12); }
                else setCalMonth(calMonth - 1);
              }}
            >
              ◀
            </button>
            <span style={{ fontSize: 'var(--font-size-large)', fontWeight: 700 }}>
              {calYear}年{calMonth}月
            </span>
            <button
              className="btn-secondary"
              style={{ width: 'auto', padding: '8px 16px', minHeight: 44 }}
              onClick={() => {
                if (calMonth === 12) { setCalYear(calYear + 1); setCalMonth(1); }
                else setCalMonth(calMonth + 1);
              }}
            >
              ▶
            </button>
          </div>

          {/* 星期标题 */}
          <div className="calendar-grid">
            {['日', '一', '二', '三', '四', '五', '六'].map(d => (
              <div key={d} style={{ textAlign: 'center', fontWeight: 700, fontSize: 'var(--font-size-small)', padding: 4 }}>
                {d}
              </div>
            ))}
          </div>

          {/* 日历格子 */}
          <div className="calendar-grid">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="calendar-cell empty" />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${calYear}-${String(calMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayData = calendarData[dateStr];
              let cellClass = 'empty';
              if (dayData) {
                if (dayData.missed > 0) cellClass = 'has-missed';
                else if (dayData.taken > 0 && dayData.taken === dayData.total) cellClass = 'has-taken';
                else if (dayData.taken > 0) cellClass = 'has-skipped';
                else if (dayData.skipped > 0) cellClass = 'has-skipped';
              }
              const isSelected = selectedDate === dateStr;
              return (
                <div
                  key={day}
                  className={`calendar-cell ${cellClass} ${isToday(day) ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                  title={dayData ? `服用${dayData.taken}/${dayData.total}次` : ''}
                  onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                >
                  {day}
                </div>
              );
            })}
          </div>

          {/* 图例 */}
          <div style={{ display: 'flex', gap: 16, marginTop: 16, justifyContent: 'center', fontSize: 'var(--font-size-small)' }}>
            <span><span style={{ display: 'inline-block', width: 16, height: 16, background: '#C8E6C9', borderRadius: 4, marginRight: 4 }} />已服用</span>
            <span><span style={{ display: 'inline-block', width: 16, height: 16, background: '#FFE0B2', borderRadius: 4, marginRight: 4 }} />已跳过 / 部分</span>
            <span><span style={{ display: 'inline-block', width: 16, height: 16, background: '#FFCDD2', borderRadius: 4, marginRight: 4 }} />有漏服</span>
          </div>

          {/* 选中日期的药物明细 */}
          {selectedDate && (
            <div style={{ marginTop: 20, borderTop: '1px solid var(--border-color)', paddingTop: 16 }}>
              <h3 style={{ fontSize: 'var(--font-size-large)', fontWeight: 700, marginBottom: 12 }}>
                {selectedDate} 用药明细
              </h3>
              {calendarData[selectedDate] && calendarData[selectedDate].items.length > 0 ? (
                calendarData[selectedDate].items.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid var(--border-color)',
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 700 }}>{item.medicine_name}</span>
                      <span style={{ marginLeft: 8, color: 'var(--text-secondary)', fontSize: 'var(--font-size-small)' }}>
                        {item.dosage} {item.unit || '片'}
                        {item.scheduled_time ? ` · ${item.scheduled_time}` : ''}
                      </span>
                    </div>
                    <span className={`tag ${item.status === 'taken' ? 'tag-success' : item.status === 'missed' ? 'tag-danger' : 'tag-warning'}`}>
                      {item.status === 'taken' ? '已服用' : item.status === 'missed' ? '已错过' : '已跳过'}
                    </span>
                  </div>
                ))
              ) : (
                <p style={{ color: 'var(--text-light)', fontSize: 'var(--font-size-small)' }}>这天没有用药记录</p>
              )}
            </div>
          )}
        </div>
      ) : (
        /* 提醒日志：记的是「系统响了没有」，跟「我吃了没有」是两件事 */
        <>
          <div className="card">
            <div className="card-title">
              <span>🔔 提醒日志</span>
              {/* 这段解释只在第一次看的时候有用，看完就是纯占地方 —— 折进气泡 */}
              <InfoTip title="这和「用药记录」有什么不一样？">
                这里记的是系统这一侧发生了什么：几点发出提醒、有没有送到手机、你按了什么。
                上面的「用药记录」记的是结果（吃了 / 漏了 / 跳过）。
              </InfoTip>
            </div>
            {/* 三个筛选摆成一行（用户反馈）。
                原来是「日期独占一行 + 事件和排序一行」，光筛选就吃掉两行；
                压成一行后下面能多显示两三条日志。
                日期那栏给 1.4 倍宽度，因为只有它要装「9月15日（5 条）」这种长文本。
                事件、排序的选项标签同步压到 2 个字，理由见文件上方 EVENT_OPTIONS 注释。 */}
            <div className="select-row" style={{ marginBottom: 12 }}>
              <select
                className="input-field select-wide"
                value={logDate}
                onChange={e => setLogDate(e.target.value)}
              >
                <option value="">全部日期</option>
                {logDays.map(d => (
                  <option key={d.log_date} value={d.log_date}>
                    {dayLabel(d.log_date)}（{d.count} 条{d.missed > 0 ? `，漏服 ${d.missed}` : ''}）
                  </option>
                ))}
              </select>
              <select
                className="input-field"
                value={logEvent}
                onChange={e => setLogEvent(e.target.value)}
              >
                {EVENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select
                className="input-field"
                value={logSort}
                onChange={e => setLogSort(e.target.value)}
              >
                <option value="time_desc">最新</option>
                <option value="time_asc">最早</option>
              </select>
            </div>
          </div>

          {logs.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state-icon">🔔</div>
                <p>还没有提醒日志哦</p>
                <p style={{ marginTop: 8, fontSize: 'var(--font-size-small)' }}>
                  有提醒后，这里会记下每一条
                </p>
              </div>
            </div>
          ) : (
            Object.entries(groupedLogs).map(([date, items]) => {
              const missedLogs = items.filter(i => i.event === 'missed').length;
              return (
                <div key={date} className="card">
                  <div className="rl-day-head">
                    <span>{dayLabel(date)}</span>
                    {/* 「条」是日志条目数，和「次」不是一回事：同一次提醒会依次留下
                        「到点 → 送达 → 漏服」多条日志。所以这里把单位写清是「条记录」，
                        漏服仍按「次」计 —— 两个数字混用单位，读起来就像数错了。 */}
                    <span className="rl-day-count">
                      {items.length} 条记录
                      {missedLogs > 0 && (
                        <b style={{ color: 'var(--danger-color)' }}> · 漏服 {missedLogs} 次</b>
                      )}
                    </span>
                  </div>
                  {items.map(l => (
                    <div key={l.id} className="rl-row">
                      <span className="rl-time">{l.log_time.slice(0, 5)}</span>
                      <span className={`rl-tag ${logTone(l.event)}`}>
                        {l.event_icon} {l.event_label}
                      </span>
                      <span className="rl-body">
                        <b>{l.medicine_name || '—'}</b>
                        {l.scheduled_time ? ` · 计划 ${l.scheduled_time}` : ''}
                        {l.detail ? <span className="rl-detail">{l.detail}</span> : null}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </>
      )}
    </div>
  );
}
